import { Translate } from '@chipinside/translate'

import { getImportFormFields } from '#/store/hooks/useForms/Import/getImportFormFields'
import { FormProps, UseFormsProps } from '#/store/hooks/useForms/types'
import { endpoint } from '#/store/services/endpoints'

export type WithFarm = { farm: string }

const importForms = {
  import: {
    'abs-monitor': ({ params }: FormProps<WithFarm>) => ({
      customFields: getImportFormFields({ type: 'abs-monitor' }),
      save: {
        controller: endpoint.import.farm.abs_monitor.reproduction(params),
        method: 'post',
      },
      submitButton: {
        title: Translate({ messageKey: 'import_item' }),
      },
      title: Translate({ messageKey: 'import_item', params: { item: 'ABS Monitor' } }),
      width: 'sm',
    }),
    'animal': ({ params }: FormProps<WithFarm>) => ({
      customFields: getImportFormFields({
        fileURL: endpoint.import.farm.animal.file(params),
        type: 'animal',
      }),
      save: {
        controller: endpoint.import.farm.animal.self(params),
        method: 'post',
      },
      submitButton: {
        title: Translate({ messageKey: 'import_item' }),
      },
      title: Translate({
        messageKey: 'import_item',
        params: { item: Translate({ lower: true, messageKey: 'animal_plural' }) },
      }),
      width: 'sm',
    }),
    'animals_a_protocol': ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      customFields: getImportFormFields({
        fileURL: endpoint.import.farm.protocols_animals.file(params),
        type: 'animals_a_protocol',
      }),
      save: {
        controller: values => {
          const protocolSlug = values?.['protocol_schedule']

          return endpoint.import.farm.protocols_animals.self({ farm: params?.farm, protocolSlug })
        },
        method: 'post',
      },
      title: Translate({
        messageKey: 'import_item',
        params: {
          item: Translate({ lower: true, messageKey: 'animals_a_protocol' }),
        },
      }),
      width: 'sm',
    }),
    'animals_into_protocol': ({
      params,
    }: FormProps<WithFarm & { protocolSlug: string; protocolName: string }>): UseFormsProps => ({
      customFields: getImportFormFields({
        fileURL: endpoint.import.farm.protocols_animals.file(params),
        type: 'animals_into_protocol',
      }),
      save: {
        controller: endpoint.import.farm.protocols_animals.self({
          farm: params?.farm,
          protocolSlug: params?.protocolSlug,
        }),
        method: 'post',
      },
      subtitle: params?.protocolName,
      title: Translate({
        messageKey: 'import_item',
        params: {
          item: Translate({ lower: true, messageKey: 'animals_into_protocol' }),
        },
      }),
      width: 'sm',
    }),
    'body_assessment': ({ params }: FormProps<WithFarm>) => ({
      customFields: getImportFormFields({
        fileURL: endpoint.import.farm.body_assessment.file(params),
        type: 'body_assessment',
      }),
      save: {
        controller: endpoint.import.farm.body_assessment.self(params),
        method: 'post',
      },
      submitButton: {
        title: Translate({ messageKey: 'import_item' }),
      },
      title: Translate({
        messageKey: 'import_item',
        params: {
          item: Translate({ lower: true, messageKey: 'body_assessment' }),
        },
      }),
      width: 'sm',
    }),
    'bovisync': ({ params }: FormProps<WithFarm>) => ({
      customFields: getImportFormFields({ type: 'bovisync', withDate: true }),
      save: {
        controller: endpoint.import.farm.bovisync.self(params),
        method: 'post',
      },
      submitButton: {
        title: Translate({ messageKey: 'import_item' }),
      },
      title: Translate({ messageKey: 'import_item', params: { item: 'Bovisync' } }),
      width: 'sm',
    }),
    'bovisync_discards': ({ params }: FormProps<WithFarm>) => ({
      customFields: getImportFormFields({ type: 'bovisync_discards' }),
      save: {
        controller: endpoint.import.farm.bovisync.discards(params),
        method: 'post',
      },
      submitButton: {
        title: Translate({ messageKey: 'import_item' }),
      },
      title: Translate({
        messageKey: 'import_item',
        params: {
          item: Translate({ messageKey: ['bovisync', 'discard_plural'], separator: ' - ' }),
        },
      }),
      width: 'sm',
    }),
    'calving': ({ params }: FormProps<WithFarm>) => ({
      customFields: getImportFormFields({
        fileURL: endpoint.import.farm.calving.file(params),
        type: 'calving',
      }),
      save: {
        controller: endpoint.import.farm.calving.self(params),
        method: 'post',
      },
      submitButton: {
        title: Translate({ messageKey: 'import_item' }),
      },
      title: Translate({
        messageKey: 'import_item',
        params: {
          item: Translate({ lower: true, messageKey: 'calving_event_plural' }),
        },
      }),
      width: 'sm',
    }),
    'ccom': () => ({
      customFields: getImportFormFields({
        fileURL: endpoint.import.system.ccom.file(),
        type: 'ccom',
      }),
      save: {
        controller: endpoint.ccom.store_many(),
        method: 'post',
      },
      submitButton: {
        title: Translate({ messageKey: 'import_item' }),
      },
      title: Translate({
        messageKey: 'import_item',
        params: { item: Translate({ messageKey: 'c_com' }) },
      }),
      width: 'sm',
    }),
    'ccom_update': () => ({
      customFields: getImportFormFields({
        fileURL: endpoint.import.system.ccom.file(),
        type: 'ccom',
      }),
      save: {
        controller: endpoint.ccom.store_many(),
        method: 'post',
      },
      submitButton: {
        title: Translate({ messageKey: 'update_item' }),
      },
      title: Translate({
        messageKey: 'update_item',
        params: { item: Translate({ messageKey: 'c_com' }) },
      }),
      width: 'sm',
    }),
    'cenv': () => ({
      customFields: getImportFormFields({
        fileURL: endpoint.import.system.cenv.file(),
        type: 'cenv',
      }),
      save: {
        controller: endpoint.import.system.cenv.self(),
        method: 'post',
      },
      submitButton: {
        title: Translate({ messageKey: 'import_item' }),
      },
      title: Translate({
        messageKey: 'import_item',
        params: { item: Translate({ messageKey: 'cow_comfort' }) },
      }),
      width: 'sm',
    }),
    'ctech': () => ({
      customFields: getImportFormFields({
        fileURL: endpoint.import.system.ctech.file(),
        type: 'ctech',
      }),
      save: {
        controller: endpoint.import.system.ctech.self(),
        method: 'post',
      },
      submitButton: {
        title: Translate({ messageKey: 'import_item' }),
      },
      title: Translate({
        messageKey: 'import_item',
        params: {
          item: Translate({ messageKey: 'c_tech' }),
        },
      }),
      width: 'sm',
    }),
    'dairy-comp': ({ params }: FormProps<WithFarm>) => ({
      customFields: getImportFormFields({ type: 'dairy-comp', withDate: true }),
      save: {
        controller: endpoint.import.farm.dairy_comp.self(params),
        method: 'post',
      },
      submitButton: {
        title: Translate({ messageKey: 'import_item' }),
      },
      title: Translate({ messageKey: 'import_item', params: { item: 'DairyComp' } }),
      width: 'sm',
    }),
    'dairy-plan': ({ params }: FormProps<WithFarm>) => ({
      customFields: getImportFormFields({ type: 'dairy-plan', withDate: true }),
      save: {
        controller: endpoint.import.farm.dairy_plan.reproduction(params),
        method: 'post',
      },
      submitButton: {
        title: Translate({ messageKey: 'import_item' }),
      },
      title: Translate({ messageKey: 'import_item', params: { item: 'DairyPlan' } }),
      width: 'sm',
    }),
    'dairy-plan-discards': ({ params }: FormProps<WithFarm>) => ({
      customFields: getImportFormFields({ type: 'dairy-plan-discards' }),
      save: {
        controller: endpoint.import.farm.dairy_plan.discards(params),
        method: 'post',
      },
      submitButton: {
        title: Translate({ messageKey: 'import_item' }),
      },
      title: Translate({
        messageKey: 'import_item',
        params: {
          item: Translate({ messageKey: ['DairyPlan', 'discard_plural'], separator: ' - ' }),
        },
      }),
      width: 'sm',
    }),
    'delpro': ({ params }: FormProps<WithFarm>) => ({
      customFields: getImportFormFields({ type: 'delpro', withDate: true }),
      save: {
        controller: endpoint.import.farm.delpro.reproduction(params),
        method: 'post',
      },
      submitButton: {
        title: Translate({ messageKey: 'import_item' }),
      },
      title: Translate({ messageKey: 'import_item', params: { item: 'Delpro' } }),
      width: 'sm',
    }),
    'delpro-discards': ({ params }: FormProps<WithFarm>) => ({
      customFields: getImportFormFields({ type: 'delpro-discards', withDate: false }),
      save: {
        controller: endpoint.import.farm.delpro.discards(params),
        method: 'post',
      },
      title: Translate({
        messageKey: 'import_item',
        params: { item: Translate({ messageKey: ['Delpro', 'discard_plural'], separator: ' - ' }) },
      }),
      width: 'sm',
    }),
    'discards': ({ params }: FormProps<WithFarm>) => ({
      customFields: getImportFormFields({
        fileURL: endpoint.import.farm.discards.file(params),
        type: 'discards',
      }),
      save: {
        controller: endpoint.import.farm.discards.self(params),
        method: 'post',
      },
      submitButton: {
        title: Translate({ messageKey: 'import_item' }),
      },
      title: Translate({ messageKey: 'import_discards' }),
      width: 'sm',
    }),
    'dry': ({ params }: FormProps<WithFarm>) => ({
      customFields: getImportFormFields({
        fileURL: endpoint.import.farm.dry.file(params),
        type: 'dry',
      }),
      save: {
        controller: endpoint.import.farm.dry.self(params),
        method: 'post',
      },
      submitButton: {
        title: Translate({ messageKey: 'import_item' }),
      },
      title: Translate({
        messageKey: 'import_item',
        params: {
          item: Translate({ lower: true, messageKey: 'dry_event_plural' }),
        },
      }),
      width: 'sm',
    }),

    'farm-animal-update': ({ params }: FormProps<WithFarm>) => ({
      customFields: getImportFormFields({
        fileURL: endpoint.import.farm.animal_update.file(params),
        type: 'farm-animal-update',
      }),
      save: {
        controller: endpoint.import.farm.animal_update.self(params),
        method: 'post',
      },
      submitButton: {
        title: Translate({ messageKey: 'import_item' }),
      },
      title: Translate({ messageKey: 'update_registration' }),
      width: 'sm',
    }),

    'file_delete': ({ params }: FormProps<WithFarm & { fileName: string }>): UseFormsProps => ({
      message: Translate({
        messageKey: 'do_you_really_want_to_item',
        params: {
          id: params?.fileName as string,
          item: Translate({
            lower: true,
            messageKey: 'remove_item',
            params: {
              item: Translate({ lower: true, messageKey: 'file' }),
            },
          }),
        },
      }),
      save: {
        controller: endpoint.import.files.file({
          farm: params?.farm,
          file: params?.fileName,
        }),
        method: 'delete',
      },
      subtitle: params?.fileName,
      title: Translate({
        messageKey: 'remove_item',
        params: {
          item: Translate({ lower: true, messageKey: 'file' }),
        },
      }),
    }),
    'health': ({ params }: FormProps<WithFarm>) => ({
      customFields: getImportFormFields({
        fileURL: endpoint.import.farm.health.file(params),
        type: 'health',
      }),
      save: {
        controller: endpoint.import.farm.health.self(params),
        method: 'post',
      },
      submitButton: {
        title: Translate({ messageKey: 'import_item' }),
      },
      title: Translate({
        messageKey: 'import_item',
        params: {
          item: Translate({ lower: true, messageKey: 'health_event_plural' }),
        },
      }),
      width: 'sm',
    }),
    'ideagri': ({ params }: FormProps<WithFarm>) => ({
      customFields: getImportFormFields({ type: 'ideagri', withDate: true }),
      save: {
        controller: (values: any) => {
          const spreadsheetType = values?.['spreadsheet-type']

          return spreadsheetType === 'production'
            ? endpoint.import.farm.ideagri.milk_production(params)
            : endpoint.import.farm.ideagri.reproduction(params)
        },
        method: 'post',
      },
      submitButton: {
        title: Translate({ messageKey: 'import_item' }),
      },
      title: Translate({ messageKey: 'import_item', params: { item: 'Ideagri' } }),
      width: 'sm',
    }),
    'leigado': ({ params }: FormProps<WithFarm>) => ({
      customFields: getImportFormFields({ type: 'leigado' }),
      save: {
        controller: endpoint.import.farm.leigado.reproduction(params),
        method: 'post',
      },
      submitButton: {
        title: Translate({ messageKey: 'import_item' }),
      },
      title: Translate({ messageKey: 'import_item', params: { item: 'Leigado' } }),
      width: 'sm',
    }),
    'lely': ({ params }: FormProps<WithFarm>) => ({
      customFields: getImportFormFields({ type: 'lely', withDate: true }),
      save: {
        controller: endpoint.import.farm.lely.reproduction(params),
        method: 'post',
      },
      submitButton: {
        title: Translate({ messageKey: 'import_item' }),
      },
      title: Translate({ messageKey: 'import_item', params: { item: 'Lely' } }),
      width: 'sm',
    }),
    'mating': ({ params }: FormProps<WithFarm>) => ({
      customFields: getImportFormFields({
        fileURL: endpoint.import.farm.mating.file(params),
        type: 'mating',
      }),
      save: {
        controller: endpoint.import.farm.mating.self(params),
        method: 'post',
      },
      submitButton: {
        title: Translate({ messageKey: 'import_item' }),
      },
      title: Translate({
        messageKey: 'import_item',
        params: {
          item: Translate({ lower: true, messageKey: 'mating' }),
        },
      }),
      width: 'sm',
    }),
    'milk_individualized': ({ params }: FormProps<WithFarm>) => ({
      customFields: getImportFormFields({
        fileURL: endpoint.import.farm.milk_control_individualized.file(params),
        type: 'milk',
        withDate: true,
      }),
      save: {
        controller: endpoint.import.farm.milk_control_individualized.self(params),
        method: 'post',
      },
      submitButton: {
        title: Translate({ messageKey: 'import_item' }),
      },
      title: Translate({
        messageKey: 'import_item',
        params: {
          item: Translate({ lower: true, messageKey: 'individual_milk_control' }),
        },
      }),
      width: 'sm',
    }),
    'milk_total': ({ params }: FormProps<WithFarm>) => ({
      customFields: getImportFormFields({
        fileURL: endpoint.import.farm.milk_control_total.file(params),
        type: 'milk',
      }),
      save: {
        controller: endpoint.import.farm.milk_control_total.self(params),
        method: 'post',
      },
      submitButton: {
        title: Translate({ messageKey: 'import_item' }),
      },
      title: Translate({
        messageKey: 'register_item',
        params: {
          item: Translate({ lower: true, messageKey: 'general_milk_control' }),
        },
      }),
      width: 'sm',
    }),
    'prepartum': ({ params }: FormProps<WithFarm>) => ({
      customFields: getImportFormFields({
        fileURL: endpoint.import.farm.prepartum.file(params),
        type: 'prepartum',
      }),
      save: {
        controller: endpoint.import.farm.prepartum.self(params),
        method: 'post',
      },
      submitButton: {
        title: Translate({ messageKey: 'import_item' }),
      },
      title: Translate({
        messageKey: 'import_item',
        params: {
          item: Translate({ lower: true, messageKey: 'pre_calving_event_plural' }),
        },
      }),
      width: 'sm',
    }),
    'reproduction': ({ params }: FormProps<WithFarm>) => ({
      customFields: getImportFormFields({
        fileURL: endpoint.import.farm.reproduction.file(params),
        type: 'reproduction',
      }),
      save: {
        controller: endpoint.import.farm.reproduction.self(params),
        method: 'post',
      },
      submitButton: {
        title: Translate({ messageKey: 'import_item' }),
      },
      title: Translate({
        messageKey: 'import_item',
        params: {
          item: Translate({ lower: true, messageKey: 'reproduction_event_plural' }),
        },
      }),
      width: 'sm',
    }),
    'reproductive-event-history': ({ params }: FormProps<WithFarm>) => ({
      customFields: getImportFormFields({
        fileURL: endpoint.import.farm.reproductive_event_history.file(params),
        type: 'reproductive-event-history',
      }),
      save: {
        controller: endpoint.import.farm.reproductive_event_history.self(params),
        method: 'post',
      },
      submitButton: {
        title: Translate({ messageKey: 'import_item' }),
      },
      title: Translate({
        messageKey: 'import_item',
        params: {
          item: Translate({ lower: true, messageKey: 'reproductive_event_history' }),
        },
      }),
      width: 'sm',
    }),
    'review': () => ({
      customFields: getImportFormFields({
        fileURL: endpoint.import.system.ctech_review.file(),
        type: 'review',
      }),
      save: {
        controller: endpoint.import.system.ctech_review.self(),
        method: 'post',
      },
      submitButton: {
        title: Translate({ messageKey: 'import_item' }),
      },
      title: Translate({ messageKey: 'source_review' }),
      width: 'sm',
    }),
    'system-bull-semen': () => ({
      customFields: getImportFormFields({
        fileURL: endpoint.import.system.bull_semen.file(),
        type: 'system-bull-semen',
      }),
      save: {
        controller: endpoint.import.system.bull_semen.self(),
        method: 'post',
      },
      submitButton: {
        title: Translate({ messageKey: 'import_item' }),
      },
      title: Translate({
        messageKey: 'import_item',
        params: { item: Translate({ lower: true, messageKey: 'bull_or_semen' }) },
      }),
      width: 'sm',
    }),
    'system-new-bull-semen': () => ({
      customFields: getImportFormFields({
        fileURL: endpoint.import.system.new_bull_semen.file(),
        type: 'system-bull-semen',
      }),
      save: {
        controller: endpoint.import.system.new_bull_semen.self(),
        method: 'post',
      },
      submitButton: {
        title: Translate({ messageKey: 'import_item' }),
      },
      title: Translate({
        messageKey: 'import_item',
        params: {
          item: Translate({
            capsule: [null, '()'],
            lower: [true, false],
            messageKey: ['bull_or_semen', 'new_item_male'],
          }),
        },
      }),
      width: 'sm',
    }),
    'uniform-agri': ({ params }: FormProps<WithFarm>) => ({
      customFields: getImportFormFields({
        type: 'uniform-agri',
        withDate: true,
      }),
      save: {
        controller: endpoint.import.farm.uniform_agri.reproduction(params),
        method: 'post',
      },
      submitButton: {
        title: Translate({ messageKey: 'import_item' }),
      },
      title: Translate({ messageKey: 'import_item', params: { item: 'UNIFORM-Agri' } }),
      width: 'sm',
    }),
    'uniform-agri-discards': ({ params }: FormProps<WithFarm>) => ({
      customFields: getImportFormFields({
        type: 'uniform-agri-discards',
        withDate: true,
      }),
      save: {
        controller: endpoint.import.farm.uniform_agri.discards(params),
        method: 'post',
      },
      submitButton: {
        title: Translate({ messageKey: 'import_item' }),
      },
      title: Translate({
        messageKey: 'import_item',
        params: {
          item: Translate({ messageKey: ['UNIFORM-Agri', 'discard_plural'], separator: ' - ' }),
        },
      }),
      width: 'sm',
    }),
  },
}

export { importForms }
